import { PlatformAppEvent } from '@wix/public-editor-platform-events';
import { EventType } from '@wix/public-editor-platform-interfaces';
import { createErrorBuilder, BaseError } from '@wix/public-editor-platform-errors';
import { ApplicationContext } from '@wix/editor-platform-contexts';

function hash() {
  const _performance = globalThis["performance"];
  if (_performance && _performance.now) {
    return _performance.now();
  }
  let result = "";
  const characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  const length = 10;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}

class ApplicationLifecycle {
  constructor(events) {
    this.events = events;
    this.subscribe();
  }
  callbacks = {};
  subscribe() {
    this.events.subscribe((event) => {
      const { type, payload } = event;
      if (this.callbacks[type]) {
        this.callbacks[type].forEach(({ fn }) => fn(payload));
      }
    });
  }
  addCallback(event, fn) {
    if (!this.callbacks[event]) {
      this.callbacks[event] = [];
    }
    const id = `${hash()}`;
    this.callbacks[event].push({
      id,
      fn
    });
    return () => {
      this.callbacks[event] = this.callbacks[event].filter(
        (cb) => cb.id !== id
      );
    };
  }
  /**
   * NOTE: currently, we return function to unsubscribe from the event,
   * probably it is better to return `this` to allow chaining
   * and provide another way to unsubscribe from events.
   */
  onEditorReady(fn) {
    return this.addCallback(PlatformAppEvent.EditorReady, fn);
  }
}

var EditorPlatformApplicationErrorCode = /* @__PURE__ */ ((EditorPlatformApplicationErrorCode2) => {
  EditorPlatformApplicationErrorCode2["ApplicationRuntimeError"] = "ApplicationRuntimeError";
  return EditorPlatformApplicationErrorCode2;
})(EditorPlatformApplicationErrorCode || {});
class EditorPlatformApplicationError extends BaseError {
  state = {};
  constructor(message, code) {
    super(message, code, "EP Application Error");
  }
  withUrl(url) {
    this.state = { ...this.state, url };
    return this;
  }
  withAppDefinitionId(appDefinitionId) {
    this.state = { ...this.state, appDefinitionId };
    return this;
  }
  withApiMethod(apiMethod) {
    this.state = { ...this.state, apiMethod };
    return this;
  }
  withApiType(apiType) {
    this.state = { ...this.state, apiType };
    return this;
  }
}
const createEditorPlatformApplicationError = createErrorBuilder(EditorPlatformApplicationError);

class ChainAPIConfiguration {
  constructor(api) {
    this.api = api;
  }
  exposePublicAPI(api) {
    this.api.public = api;
    return this;
  }
  exposePrivateAPI(api) {
    this.api.private = api;
    return this;
  }
}
class EditorPlatformApplication {
  constructor(type, context) {
    this.type = type;
    this.#context = context;
    this.appDefinitionId = this.#context.getAppDefinitionId();
    const events = this.#context.getEvents();
    this.lifecycle = new ApplicationLifecycle(events);
    events.addEventListener(EventType.removeAppCompleted, (e) => {
      if (e.appDefinitionId === this.appDefinitionId) {
        this.state = "REMOVED";
      }
    });
    const applicationRegister = __APPLICATION_REGISTRY_KEY;
    applicationRegister(this);
    events.notify({
      type: PlatformAppEvent.ApplicationInit,
      payload: {}
    });
  }
  appDefinitionId;
  state = "READY";
  lifecycle;
  #applicationAPI = {};
  #chainAPIConfiguration = new ChainAPIConfiguration(
    this.#applicationAPI
  );
  #manifest;
  #context;
  get context() {
    return this.#context;
  }
  get events() {
    return this.#context.getEvents();
  }
  setManifest(manifest) {
    this.#manifest = manifest;
  }
  getManifest() {
    return this.#manifest;
  }
  exposePublicAPI(api) {
    return this.#chainAPIConfiguration.exposePublicAPI(api);
  }
  exposePrivateAPI(api) {
    return this.#chainAPIConfiguration.exposePrivateAPI(api);
  }
  getPublicAPI() {
    return this.#applicationAPI.public;
  }
  getPrivateAPI() {
    return this.#applicationAPI.private;
  }
  ready() {
  }
  buildApplicationError(message) {
    return createEditorPlatformApplicationError(
      EditorPlatformApplicationErrorCode.ApplicationRuntimeError,
      message
    ).withAppDefinitionId(this.appDefinitionId);
  }
}

var ApplicationType = /* @__PURE__ */ ((ApplicationType2) => {
  ApplicationType2["EditorAddon"] = "EDITOR_ADDON";
  ApplicationType2["Platform"] = "PLATFORM";
  return ApplicationType2;
})(ApplicationType || {});

class WixEditorPlatformApplication extends EditorPlatformApplication {
  static async create() {
    return new WixEditorPlatformApplication(
      await ApplicationContext.getInstance()
    );
  }
  constructor(context) {
    super(ApplicationType.Platform, context);
  }
}

class WixEditorPlatformAddon extends EditorPlatformApplication {
  static async create() {
    const context = await ApplicationContext.getInstance();
    return new WixEditorPlatformAddon(context);
  }
  constructor(context) {
    super(ApplicationType.EditorAddon, context);
  }
}

export { ApplicationType, EditorPlatformApplication, WixEditorPlatformAddon, WixEditorPlatformApplication };
//# sourceMappingURL=index.js.map
